import { Formik, Form } from "formik";
import type { UseMutationResult } from "@tanstack/react-query";
import type { Stage2GridProps } from "../../PreComplianceStep";
import type {
	ComplianceGrid,
	ComplianceQuestion,
	SaveComplianceGridRequest,
} from "newui/features/Rfx/Evaluation/types/compliance";
import { SupplierComplianceAccordion } from "../SupplierComplianceAccordion";
import type { SupplierMeta } from "../SupplierComplianceAccordionItem";
import { allSuppliersDecided } from "../compliance-grid.helpers";
import {
	complianceGridSchema,
	type ComplianceGridFormValues,
} from "../compliance-grid.schema";

export interface ComplianceGridFormProps {
	initialValues: ComplianceGridFormValues;
	questions: ComplianceQuestion[];
	supplierMeta: SupplierMeta[];
	locked: boolean;
	skipped: boolean;
	onBack: Stage2GridProps["onBack"];
	onComplete: Stage2GridProps["onComplete"];
	saveGrid: UseMutationResult<ComplianceGrid, Error, SaveComplianceGridRequest>;
}

export const ComplianceGridForm = ({
	initialValues,
	questions,
	supplierMeta,
	locked,
	skipped,
	onBack,
	onComplete,
	saveGrid,
}: ComplianceGridFormProps) => {
	const handleSubmit = (values: ComplianceGridFormValues) => {
		const payload: SaveComplianceGridRequest = {
			suppliers: values.suppliers.map((s) => ({
				invite_id: s.invite_id,
				answers: s.answers.map((a) => ({
					rfx_evaluation_compliance_question_id:
						a.rfx_evaluation_compliance_question_id,
					// Pass is_compliant through untouched (boolean | null). null = unanswered;
					// coercing to false would corrupt unanswered rows on non-evaluated
					// suppliers. The yup gate guarantees non-null for evaluated suppliers,
					// and the backend ignores this field when is_evaluated=false.
					is_compliant: a.is_compliant,
				})),
				comments: s.comments,
				// By the time the form can be submitted every supplier has an explicit
				// Yes/No decision (the schema's decision-required gate enforces it), so
				// is_evaluated is never null here.
				is_evaluated: s.is_evaluated === true,
			})),
		};

		saveGrid.mutate(payload, {
			onSuccess: () => {
				onComplete();
			},
		});
	};

	return (
		<div data-testid="stage2-compliance-grid">
			{locked && (
				<div
					className="mb-4 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800"
					data-testid="compliance-grid-locked-banner"
				>
					Compliance check is locked — scoring has started.
				</div>
			)}

			{/*
				No enableReinitialize: a background refetch mid-edit would wipe the
				RFx Lead's in-progress toggles/comments. initialValues is seeded once
				from the first successful GET; the save mutation invalidates the cache
				and the parent advances past Step 0 on success, so we never need to
				re-seed this form while it is mounted.
			*/}
			<Formik
				initialValues={initialValues}
				validationSchema={complianceGridSchema}
				onSubmit={handleSubmit}
			>
				{({ values, isValid }) => {
					const allDecided = allSuppliersDecided(values.suppliers);

					const canProceed = locked ? false : skipped || allDecided;

					return (
						<Form>
							<SupplierComplianceAccordion
								questions={questions}
								suppliers={supplierMeta}
								locked={locked}
							/>

							{!allDecided && !skipped && (
								<p
									className="mt-3 text-sm text-amber-700"
									data-testid="compliance-grid-proceed-hint"
								>
									Every supplier must be marked Compliant or Non-Compliant for
									Evaluation before proceeding.
								</p>
							)}

							<div className="mt-6 flex gap-3">
								<button
									type="button"
									onClick={onBack}
									className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
									data-testid="compliance-grid-back"
								>
									Back
								</button>
								{!locked && (
									<button
										type="submit"
										disabled={!canProceed || !isValid || saveGrid.isPending}
										className="rounded-lg bg-[#0031DD] px-5 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
										data-testid="compliance-grid-proceed"
									>
										{saveGrid.isPending ? "Saving…" : "Proceed"}
									</button>
								)}
							</div>
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};
